import type { ReactNode } from 'react'
import Panel from '@/components/layout/Panel'

/**
 * One block of Settings, drawn as the site's glass band.
 *
 * The page used to be three stacked lists with headings in three different
 * sizes and rows styled a little differently in each. The shell is here so a
 * fourth section cannot invent a fourth look — and the shell itself now lives
 * in `layout/Panel`, because Settings was not the only page drawing this band.
 */
export default function SettingsSection({ title, description, children }: {
  title: string
  description?: string
  children: ReactNode
}) {
  return <Panel title={title} hint={description}>{children}</Panel>
}

/* ── Shared row chrome ─────────────────────────────────────────────────────── */

/** An existing entry. Matches the dashboard's card fill. */
export const settingsRow =
  'flex items-center gap-3 rounded-sm border border-white/10 bg-white/[0.035] px-3 py-2 transition-colors hover:border-white/15'

export const settingsInput =
  'h-8 rounded border border-white/10 bg-white/5 px-2 text-sm text-foreground placeholder:text-foreground/30 focus:border-primary focus:outline-none'

/** The "add one" strip, dashed so it reads as a blank waiting to be filled. */
export const settingsAddRow =
  'mt-3 flex flex-wrap items-center gap-2 rounded-sm border border-dashed border-white/15 bg-white/[0.02] p-2'

export const settingsAddButton =
  'flex h-8 shrink-0 items-center gap-1.5 rounded bg-primary/80 px-3 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary disabled:opacity-40'

/** Icon-only action at the end of a row (edit, delete, reorder). */
export const settingsIconButton =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground/40 transition-colors hover:bg-white/8 hover:text-foreground disabled:opacity-20 disabled:hover:bg-transparent'

export function SettingsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-11 animate-pulse rounded-sm bg-white/5" />
      ))}
    </div>
  )
}

export function SettingsEmpty({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-sm border border-dashed border-white/10 py-6 text-center text-xs text-muted-foreground/70">
      {children}
    </p>
  )
}
